/**
 * Category hubs `/{cc}/{lang}/category/:l1/:l2?` (web plan W3.1, master D-5):
 * the rules the SPA page (src/pages/CategoryPage.tsx) and the SEO server
 * (server/routes.mjs) share, so both read the backend's `categoryPage` (B3.7b)
 * the same way.
 *
 * - Levels 1–2 only: `/category/{l1}` and `/category/{l1}/{l2}`. A level-3
 *   category has no page of its own — its `urlPath` is its level-2 parent's
 *   hub — and a path with a third segment matches no route (404). Which
 *   combinations exist is the backend's answer: an unknown slug, or an `l2`
 *   that is not a child of `l1`, is NOT_FOUND there, a 404 here.
 * - Robots come only from the backend's `seo.robots` (HUB_HAS_CONTENT →
 *   index; HUB_BELOW_THRESHOLD → `noindex, follow`; HUB_NOT_LISTED_YET while
 *   its PUBLIC_HUB_TYPES leaves categories out → the robots the page will
 *   have, but not listed in the sitemap).
 * - Another hub is linked from a hub only while that hub's own
 *   `seo.indexable` is true (D-5: a hub under its threshold is not linked from
 *   other hubs; `HUB_NOT_LISTED_YET` is not indexable either). Entity pages
 *   (brand, store, shop, promotion) may still link any hub (their category
 *   chips).
 * - `?page=N` pages the live sales, LIST_PAGE_SIZE a page, like the catalog
 *   lists (web plan W0.11): the backend returns the whole list, the page is a
 *   slice of it; page 1 is the bare path.
 *
 * Pure (no React, no `window`).
 */
import type { Brand, Category, EntitySeo, Promotion } from '@/types';
import { sitePath } from './hubLinks';
import { localeKey, type Locale } from './locales';
import { brandSeoName, cleanName } from './text';
import { LIST_PAGE_SIZE, lastListPage, localePath } from './urls';

/** The backend's `categoryPage` answer, as the SPA and the server select it (`CATEGORY_PAGE`). */
export interface CategoryHubPage {
  /** The hub's canonical path: serve only there, 301 to it from any other spelling. */
  urlPath: string;
  category: Category;
  /** Active now, tagged at the hub or below it. */
  livePromotions: readonly Promotion[];
  upcomingPromotions: readonly Promotion[];
  /** Most followed first. */
  brands: readonly Brand[];
  brandCount: number;
  /** A level-1 hub's indexable level-2 hubs, by title ([] on a level-2 hub). */
  subCategories: readonly Category[];
  seo: EntitySeo;
}

/** One route segment: decoded, trimmed, lower case; null when it is not a usable slug. */
function hubSegment(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  let value: string;
  try {
    value = decodeURIComponent(raw);
  } catch {
    return null;
  }
  value = value.trim().toLowerCase();
  // A slug is one path segment: no separators, no whitespace or control characters.
  if (!value || /[/\\?#\s\p{Cc}]/u.test(value)) return null;
  return value;
}

/**
 * The backend's `categoryPage(path)` key for the route's segments: `l1` or
 * `l1/l2` (lower case). Null for segments no category can have: the page is
 * a 404 without asking the backend.
 */
export function categoryHubKey(l1: string | null | undefined, l2?: string | null): string | null {
  const first = hubSegment(l1);
  if (!first) return null;
  if (l2 === undefined || l2 === null) return first;
  const second = hubSegment(l2);
  return second ? `${first}/${second}` : null;
}

/** `categoryPage(path, locale)` variables: the one place the SPA and the SEO server build them. */
export function categoryPageVariables(key: string, locale: Locale): { path: string; locale: string } {
  return { path: key, locale: localeKey(locale) };
}

/** The hub's label in titles and H1s: the staff SEO label (backend B3.4), else the category's title. */
export function categoryHubLabel(category: Pick<Category, 'title' | 'seoLabel'>): string {
  return cleanName(category.seoLabel) || cleanName(category.title) || category.title;
}

/** The category's own name ("{Category} brands in {Country}", sub-category links). */
export function categoryName(category: Pick<Category, 'title'>): string {
  return cleanName(category.title) || category.title;
}

/**
 * The hub path for a category's slug ancestry (`all/automotive/car-care/oil`):
 * the leading `all/` seed root is dropped and only levels 1-2 count (a
 * level-3 category links its level-2 parent's hub, never a third segment).
 * Null when no slug is left.
 */
export function categoryHubSlugs(path: string | null | undefined): string | null {
  if (typeof path !== 'string') return null;
  const parts = path.split('/').map((p) => p.trim()).filter(Boolean);
  if (parts[0]?.toLowerCase() === 'all') parts.shift();
  const slugs = parts.slice(0, 2).map(hubSegment);
  if (slugs.length === 0 || slugs.includes(null)) return null;
  return slugs.join('/');
}

/**
 * The one internal link to a category: its hub `/{cc}/{lang}/category/{l1}[/{l2}]`.
 * The backend's `urlPath` is canonical when the category was fetched with it;
 * otherwise (data from before `urlPath`, or a picker row) the hub is built from
 * the slug `path` in the ROUTE's locale (never a default country). Null only
 * for a category with neither. Hub routes always carry the locale prefix, so
 * this is `localePath`, not `catalogPath`.
 */
export function categoryHubPath(
  category: Pick<Category, 'urlPath' | 'path'> | null | undefined,
  locale: Locale,
): string | null {
  if (!category) return null;
  const fromBackend = sitePath(category.urlPath);
  if (fromBackend) return fromBackend;
  const slugs = categoryHubSlugs(category.path);
  return slugs ? localePath(locale, `/category/${slugs.split('/').map(encodeURIComponent).join('/')}`) : null;
}

// The D-5 link rule lives in a dependency-free module the footer can import.
export { linkedFromHubs, sitePath } from './hubLinks';

/**
 * The brands with the most live offers in the category, best first (ties:
 * the first one listed), at most `max`: the "{Top 3 brands}" of the title.
 */
export function topCategoryBrands(promotions: readonly Promotion[] | null | undefined, max = 3): string[] {
  const counts = new Map<string, { name: string; count: number; first: number }>();
  (promotions ?? []).forEach((promotion, index) => {
    const name = brandSeoName(promotion.brand);
    if (!name) return;
    const key = promotion.brand?.id || name.toLowerCase();
    const row = counts.get(key);
    if (row) row.count += 1;
    else counts.set(key, { name, count: 1, first: index });
  });
  return [...counts.values()]
    .sort((a, b) => b.count - a.count || a.first - b.first)
    .slice(0, max)
    .map((row) => row.name);
}

/** The hub's last `?page=` (live sales, LIST_PAGE_SIZE a page; an empty list still has page 1). */
export function categoryHubLastPage(hub: Pick<CategoryHubPage, 'livePromotions'>): number {
  return lastListPage(hub.livePromotions.length);
}

/** The live sales page `page` shows. */
export function categoryHubPromotionsOn(
  hub: Pick<CategoryHubPage, 'livePromotions'>,
  page: number,
): readonly Promotion[] {
  const n = Number.isSafeInteger(page) && page >= 1 ? page : 1;
  return hub.livePromotions.slice((n - 1) * LIST_PAGE_SIZE, n * LIST_PAGE_SIZE);
}

/** The variables of `TopCategories`: the page's country when it is not the default one (their `urlPath` and `seo` are that country's). */
export function topCategoriesVariables(country: string | undefined): { country?: string } {
  return country ? { country } : {};
}
